import {useState, useEffect, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view} from '@sail/ui';
import {
  Conversation,
  StripeAssistantLiveAnswersSource,
} from '@stripe-internal/conversations-client';
import {LoadingText} from '../../Conversation/LoadingText';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from './InlineLinkClickHandler';
import {MarkdownText} from './MarkdownText';
import {StreamingBuffer} from './StreamingBuffer';
import {SourcesDropdown} from './SourcesDropdown';
import {AgentPlanCard} from './AgentPlanCard';

const messages = defineMessages({
  streaming_failed: {
    id: 'conversations_ui.live_answers_response.streaming_failed',
    description:
      'Response text shown to users when an error occurs returning a response',
    defaultMessage: 'Sorry, an error occurred while processing your question.',
  },
  was_halted: {
    id: 'conversations_ui.live_answers_response.was_halted',
    description:
      'Response text shown to users when we stopped streaming due to blocked content',
    defaultMessage:
      "I wasn't able to answer this question. Feel free to ask another or try rephrasing it for better results.",
  },
});

export type LiveAnswersResponseProps = {
  conversation: Conversation;
  llmConversationId: string;
  messageId: string;
  cacheUuid: string;
  cachedContent?: string | null;
  sources?: Array<StripeAssistantLiveAnswersSource>;
  bufferChunkSize?: number;
  onComplete?: (content: string) => void;
  isDone?: boolean;
};

export type AgentPlanItem = {
  uuid: string;
  step: string;
};

type AgentCallResponse = {
  id?: string | undefined | null;
  object?: string | undefined | null;
  name?: string | undefined | null;
  url?: string | undefined | null;
  unit_amount?: number | undefined | null;
  currency?: string | undefined | null;
  data?: {
    id?: string | undefined | null;
    object: string | undefined | null;
    name?: string | undefined | null;
    url?: string | undefined | null;
    unit_amount?: number | undefined | null;
    currency?: string | undefined | null;
  }[];
  plan?: AgentPlanItem[];
  plan_short_title?: string;
};

type AgentCall = {
  tool_id: string;
  tool_name: string;
  tool_args: string;
  timestamp: number;
  has_response: boolean;
  response: AgentCallResponse | AgentCallResponse[];
};

const STREAMING_POLL_INTERVAL_MS = 1000;
const BUFFER_FLUSH_INTERVAL_MS = 20;
const BUFFER_CHUNK_SIZE = 5;

// Once streaming finishes, the conversation seems to remount this
// component. We cache the content so once it remounts, it has the
// full content already available. We will work to continue improving
// this and in the future this may not be needed
const STREAMING_CONTENT_CACHE = new Map<string, string>();
const CONVERSATION_CACHE = new WeakMap<
  Conversation,
  typeof STREAMING_CONTENT_CACHE
>();

function getExistingContent(
  conversation: Conversation,
  llmConversationId: string,
) {
  return CONVERSATION_CACHE.get(conversation)?.get(llmConversationId);
}

export const LiveAnswersResponse = ({
  conversation,
  llmConversationId,
  messageId,
  cacheUuid,
  cachedContent,
  sources,
  bufferChunkSize = BUFFER_CHUNK_SIZE,
  onComplete,
  isDone,
}: LiveAnswersResponseProps) => {
  // Content can be cached either on the server or client. It's
  // important to cache on the client too in case the component
  // remounts; we never want to call `streamText` again if streaming
  // has already been completed because the conversation might be
  // advanced again.
  const [loading, setLoading] = useState<boolean>(!cachedContent);
  const [canShowSources, setCanShowSources] = useState<boolean>(
    !!cachedContent,
  );
  const [doneStreaming, setDoneStreaming] = useState<boolean>(!!cachedContent);
  const [cancelled, setCancelled] = useState<boolean>(false);
  const [failure, setFailure] = useState<boolean>(false);
  const [content, setContent] = useState<string>(
    cachedContent || getExistingContent(conversation, llmConversationId) || '',
  );
  const [agentCalls, setAgentCalls] = useState<AgentCall[]>([]);

  const analytics = useAnalytics();
  const intl = useIntl();

  const conversationCache = useMemo(() => {
    let cache = CONVERSATION_CACHE.get(conversation);
    if (!cache) {
      cache = new Map();
      CONVERSATION_CACHE.set(conversation, cache);
    }
    return cache;
  }, [conversation]);

  useEffect(() => {
    const {client} = conversation;

    // No need to stream, content already exists
    if (cachedContent || doneStreaming) {
      return;
    }

    const controller = new AbortController();

    const buffer = new StreamingBuffer({
      chunkSize: bufferChunkSize,
      flushIntervalMs: BUFFER_FLUSH_INTERVAL_MS,
      onFlush: (c: string) => {
        if (controller.signal.aborted) {
          return;
        }

        setContent(c);

        const existingCachedContent = conversationCache.get(llmConversationId);
        if (
          !existingCachedContent ||
          (existingCachedContent &&
            buffer.content.length > existingCachedContent.length)
        ) {
          conversationCache.set(llmConversationId, c);
        }
      },
      onComplete: (bufferContent: string) => {
        if (controller.signal.aborted) {
          return;
        }

        setDoneStreaming(true);

        if (onComplete) {
          // Only show the sources if the response was actually answerable
          onComplete(bufferContent);
        }
      },
    });

    // TODO: This is used for analytics
    // const startTime = Date.now();

    let nextOffset = 0;

    let timeout: ReturnType<typeof setTimeout> | undefined;
    async function getNextChunk() {
      // TODO: We need to handle the case where the text has already
      // been streamed before (if you refresh the page and the
      // conversation resumes). This will depend on the backend
      // somehow sending us the full text and we'll figure out how to
      // pick it up

      const res = await client.streamText({
        conversationId: conversation.id,
        // Effect ids are always the same as message ids
        effectId: messageId,
        streamId: llmConversationId,
        cacheUuid,
        offset: nextOffset,
        signal: controller.signal,
      });

      if (controller.signal.aborted) {
        return;
      }

      setLoading(false);

      if (res.code === 200 && res.data?.type === 'success') {
        const {data} = res;

        // if we've previously marked this message as done, don't emulate streaming
        if (isDone && data.is_complete) {
          // don't let the buffer stream anymore content
          controller.abort();

          // set the content to the full content
          setContent(data.content);
          return;
        }

        nextOffset = data.next_offset;
        buffer.push(data.content);

        if (data.is_complete || data.was_halted || data.not_answerable) {
          // TODO: Should we use the same name as before?
          // analytics.action(
          //   'universal_help_live_answers_frontend_streaming_complete',
          //   {
          //     live_answers_ui_rendering_total_time: Date.now() - startTime,
          //   },
          // );

          if (data.was_halted) {
            setContent('');
            setCancelled(true);
            buffer.cancel();
          } else {
            buffer.complete();

            setCanShowSources(!data.not_answerable);

            if (data.agent_calls && data.agent_calls.length > 0) {
              setAgentCalls(data.agent_calls);
            }
          }
        } else {
          timeout = setTimeout(getNextChunk, STREAMING_POLL_INTERVAL_MS);
        }
      } else {
        // TODO: What params should we log here? Should we use the
        // same name as before when the params will be different?
        // analytics.action(
        //   'universal_help_live_answers_frontend_streaming_failed',
        // );
        setFailure(true);
      }
    }

    getNextChunk();

    return () => {
      controller.abort();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    analytics,
    bufferChunkSize,
    conversation,
    cacheUuid,
    cachedContent,
    llmConversationId,
    messageId,
    onComplete,
    conversationCache,
    isDone,
    doneStreaming,
  ]);

  const text = failure
    ? intl.formatMessage(messages.streaming_failed)
    : content.trim();

  const sendMessage = async (messageText: string) => {
    const message = conversation.buildTextMessage(messageText);
    await conversation.sendMessage(message);
  };

  const renderAgentPlanCard = () => {
    let title;
    let items;

    const agentPlanCall = agentCalls.find(
      (call) => call.tool_name === 'show_plan_for_user_confirmation',
    );

    if (agentPlanCall) {
      const planResponse = agentPlanCall.response as AgentCallResponse;
      title = planResponse.plan_short_title;
      items = planResponse.plan;
    }

    const lastMessageId = conversation.mostRecentMessage()?.id;

    if (title && items && doneStreaming) {
      return (
        <AgentPlanCard
          title={title}
          items={items}
          sendMessage={sendMessage}
          loading={loading}
          isLastMessage={lastMessageId === messageId}
        />
      );
    }

    return null;
  };

  const showSources = (isDone || doneStreaming) && canShowSources;

  return (
    <view.div css={{stack: 'y', gap: 'large'}}>
      {text !== '' ? (
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(
              'universal_help_live_answers_frontend_inline_link_clicked',
              {
                live_answers_source_url: href,
                live_answers_source_title: innerText,
                live_answers_source_context: parentInnerText,
              },
            );
          }}
        >
          <MarkdownText body={text} />
        </InlineLinkClickHandler>
      ) : null}
      {renderAgentPlanCard()}
      {cancelled ? <div>{intl.formatMessage(messages.was_halted)}</div> : null}
      {showSources && sources && sources.length > 0 ? (
        <SourcesDropdown sources={sources} />
      ) : null}
      {loading ? <LoadingText /> : null}
    </view.div>
  );
};
